import { useEffect, useRef, type ReactNode } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useServices } from '@domain/services.providers';
import { useThemeStore } from '@shared/stores/theme.store';
import { i18n } from '@shared/i18n/i18n.config';

type Props = {
  readonly children?: ReactNode;
};

/**
 * Mount-once hydrator: after the user authenticates, fetch their
 * server-side UI preferences and push `theme` into the Zustand
 * store via `hydrateFromProfile`. A read-before-paint script in
 * `index.html` already applies any locally-cached theme before
 * React boots, so this step is purely about picking up remote
 * changes (e.g. the user switched theme on another device).
 */
export function PreferencesHydrator({ children }: Props): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const { userPreferencesService } = useServices();
  const hydrateFromProfile = useThemeStore((s) => s.hydrateFromProfile);
  const didRun = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || didRun.current) {
      return;
    }
    didRun.current = true;
    let cancelled = false;
    void userPreferencesService
      .get()
      .then((prefs) => {
        if (!cancelled) {
          hydrateFromProfile(prefs.theme);
          if (i18n.language !== prefs.language) {
            void i18n.changeLanguage(prefs.language);
          }
        }
      })
      .catch((error: unknown) => {
        console.warn('[user-preferences] hydrate failed', error);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userPreferencesService, hydrateFromProfile]);

  return <>{children}</>;
}
